import { useCallback, useState } from "react";
import {
  AttachMoneyOutlined,
  BlockOutlined,
  CancelOutlined,
  CheckCircleOutline,
  EventAvailableOutlined,
  EventBusyOutlined,
  ScheduleOutlined,
} from "@mui/icons-material";
import { Box, TableCell, TableSortLabel, Tooltip, Typography } from "@mui/material";
import type { ReactElement, ReactNode } from "react";
import type { Theme } from "@mui/material";
import type { SystemStyleObject } from "@mui/system";
import type {
  SituationDisplayMeta,
  SituationIconKey,
  SituationTone,
} from "./consultationSituationLabels";

export const statsTableHeadCellSx: SystemStyleObject<Theme> = {
  fontWeight: 700,
  fontSize: "1.1rem",
  lineHeight: 1.3,
  color: "text.primary",
  py: 1.5,
  borderBottomWidth: 2,
};

export const statsTableCellTextSx: SystemStyleObject<Theme> = {
  fontSize: "1.05rem",
  lineHeight: 1.45,
};

export const statsTableActionIconSx = { fontSize: 28 };
export const statsTableActionButtonSx = { p: 1.25 };

export const statsTableContainerSx: SystemStyleObject<Theme> = {
  width: "100%",
  maxWidth: "100%",
  overflowX: "auto",
  WebkitOverflowScrolling: "touch",
};

export const statsTableSx: SystemStyleObject<Theme> = {
  width: "100%",
  tableLayout: "fixed",
  minWidth: 938,
};

const statsTableProtectedCellSx: SystemStyleObject<Theme> = {
  overflow: "visible",
  whiteSpace: "nowrap",
  verticalAlign: "middle",
  position: "relative",
  zIndex: 1,
  bgcolor: "background.paper",
  px: 1,
};

export const statsTableDateCellSx: SystemStyleObject<Theme> = {
  ...statsTableProtectedCellSx,
  width: 118,
  minWidth: 118,
  maxWidth: 118,
  overflow: "visible",
  textOverflow: "clip",
};

export const statsTableDateTextSx: SystemStyleObject<Theme> = {
  ...statsTableCellTextSx,
  whiteSpace: "nowrap",
  overflow: "visible",
  textOverflow: "clip",
};

export const statsTableTimeCellSx: SystemStyleObject<Theme> = {
  ...statsTableProtectedCellSx,
  width: 72,
  minWidth: 72,
  maxWidth: 72,
  overflow: "visible",
  textOverflow: "clip",
};

export const statsTableTimeTextSx: SystemStyleObject<Theme> = {
  ...statsTableCellTextSx,
  whiteSpace: "nowrap",
  overflow: "visible",
  textOverflow: "clip",
};

export const statsTableSituationCellSx: SystemStyleObject<Theme> = {
  ...statsTableProtectedCellSx,
  width: 158,
  minWidth: 158,
  maxWidth: 158,
};

export const statsTableTruncatedCellSx: SystemStyleObject<Theme> = {
  maxWidth: 0,
  overflow: "hidden",
  textOverflow: "ellipsis",
  whiteSpace: "nowrap",
  verticalAlign: "middle",
};

export const statsTablePatientCellSx: SystemStyleObject<Theme> = {
  ...statsTableTruncatedCellSx,
  minWidth: 168,
};

export type StatsSortDirection = "asc" | "desc";

export function comparePtBrStrings(a: string, b: string) {
  return a.localeCompare(b, "pt-BR", { sensitivity: "base" });
}

export function useStatsTableSort<T extends string>(defaultField?: T | null) {
  const [sortField, setSortField] = useState<T | null>(defaultField ?? null);
  const [sortDirection, setSortDirection] = useState<StatsSortDirection>("asc");

  const requestSort = useCallback((field: T) => {
    setSortField((current) => {
      if (current === field) {
        setSortDirection((direction) =>
          direction === "asc" ? "desc" : "asc"
        );
        return field;
      }
      setSortDirection("asc");
      return field;
    });
  }, []);

  const resetSort = useCallback((field?: T | null) => {
    setSortField(field ?? null);
    setSortDirection("asc");
  }, []);

  return { sortField, sortDirection, requestSort, resetSort };
}

interface StatsSortableTableHeadCellProps<T extends string> {
  label: ReactNode;
  field: T;
  sortField: T | null;
  sortDirection: StatsSortDirection;
  onSort: (field: T) => void;
  align?: "left" | "center" | "right";
  width?: string | number;
}

export function StatsSortableTableHeadCell<T extends string>({
  label,
  field,
  sortField,
  sortDirection,
  onSort,
  align = "left",
  width,
}: StatsSortableTableHeadCellProps<T>) {
  const active = sortField === field;

  return (
    <TableCell
      align={align}
      sortDirection={active ? sortDirection : false}
      sx={{
        ...statsTableHeadCellSx,
        width,
        "& .MuiTableSortLabel-root": {
          fontWeight: 700,
          fontSize: "1.1rem",
          lineHeight: 1.3,
          color: "text.primary",
        },
        "& .MuiTableSortLabel-icon": {
          opacity: active ? 1 : 0.35,
        },
      }}
    >
      <TableSortLabel
        active={active}
        direction={active ? sortDirection : "asc"}
        onClick={() => onSort(field)}
      >
        {label}
      </TableSortLabel>
    </TableCell>
  );
}

const iconMap: Record<SituationIconKey, typeof EventAvailableOutlined> = {
  eventAvailable: EventAvailableOutlined,
  schedule: ScheduleOutlined,
  eventBusy: EventBusyOutlined,
  checkCircle: CheckCircleOutline,
  cancel: CancelOutlined,
  block: BlockOutlined,
  attachMoney: AttachMoneyOutlined,
};

const toneColorMap: Record<SituationTone, string> = {
  success: "success.main",
  warning: "warning.main",
  error: "error.main",
  info: "info.main",
  neutral: "text.secondary",
};

function SituationIcon({
  iconKey,
  tone,
}: {
  iconKey: SituationIconKey;
  tone: SituationTone;
}) {
  const Icon = iconMap[iconKey];
  return (
    <Icon
      sx={{ fontSize: "1.125rem", color: toneColorMap[tone], flexShrink: 0 }}
      aria-hidden
    />
  );
}

export function ConsultationSituationCell({
  meta,
  compact = false,
}: {
  meta: SituationDisplayMeta;
  compact?: boolean;
}) {
  const content: ReactElement = (
    <Box
      sx={{
        display: "inline-flex",
        alignItems: "center",
        justifyContent: "center",
        gap: 0.5,
        px: meta.emphasized ? 0.85 : 0.65,
        py: 0.3,
        borderRadius: 1,
        border: meta.emphasized ? "1px dashed" : "1px solid",
        borderColor: meta.emphasized ? "divider" : "transparent",
        bgcolor: meta.emphasized ? "action.hover" : "transparent",
        flexShrink: 0,
        whiteSpace: "nowrap",
        maxWidth: "none",
        lineHeight: 1,
      }}
    >
      <SituationIcon iconKey={meta.iconKey} tone={meta.tone} />
      <Typography
        component="span"
        sx={{
          fontSize: compact ? "0.78rem" : "0.82rem",
          fontWeight: 700,
          lineHeight: 1.2,
          color: "text.primary",
          whiteSpace: "nowrap",
        }}
      >
        {meta.shortLabel}
      </Typography>
    </Box>
  );

  return (
    <Tooltip title={meta.label} placement="top">
      <Box
        component="span"
        sx={{
          display: "inline-flex",
          justifyContent: "center",
          width: "100%",
          overflow: "visible",
        }}
      >
        {content}
      </Box>
    </Tooltip>
  );
}
